import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Package, X, Palette, Ruler, Briefcase, Upload, Image, Eye, EyeOff, FolderTree, Tag } from 'lucide-react';
import AdminCategories from './AdminCategories';
import RichTextEditor from '../../components/RichTextEditor';
import { isoToLocalInput, localInputToIso } from '../../utils/datetime';

// Resolve /api/uploads/... to full backend URL; absolute URLs returned unchanged.
const resolveImageUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

// ─── Tag Input Component ──────────────────────────
function TagInput({ tags, onChange, placeholder, testId }) {
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const val = inputValue.trim();
    if (val && !tags.includes(val)) {
      onChange([...tags, val]);
    }
    setInputValue('');
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-sm px-2.5 py-1 rounded-full"
            data-testid={`${testId}-tag-${i}`}
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(i)}
              className="hover:text-red-500 transition-colors"
              data-testid={`${testId}-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={addTag} data-testid={`${testId}-add-btn`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Size + Price Add Input ──────────────────────────
function SizeAddInput({ existingSizes, onAdd }) {
  const [value, setValue] = useState('');
  const submit = () => {
    const v = value.trim();
    if (v && !existingSizes.includes(v)) {
      onAdd(v);
      setValue('');
    }
  };
  return (
    <div className="flex gap-2 mt-3">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="T.ex. 10x15cm, A4, 50x70cm"
        className="h-9 text-sm flex-1"
        data-testid="new-prod-size"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={submit}
        className="h-9 shrink-0"
        data-testid="add-size-btn"
      >
        <Plus className="w-4 h-4 mr-1" /> Lägg till
      </Button>
    </div>
  );
}

const AdminProducts = () => {
  const { getAuthHeaders } = useAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('products'); // 'products' | 'categories'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showBulkSaleModal, setShowBulkSaleModal] = useState(false);
  const [bulkSaleData, setBulkSaleData] = useState({
    category: 'all',
    discount_percent: '20',
    sale_starts_at: '',
    sale_ends_at: '',
  });
  const [bulkSaleBusy, setBulkSaleBusy] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    long_description: '',
    price: '',
    old_price: '',
    sale_starts_at: '',
    sale_ends_at: '',
    images: [''],
    colors: [],
    sizes: [],
    model_type: 'mug',
    quantity_prices: [],
    color_images: {},
    size_images: {},
    size_quality_prices: [],
    available_sizes: [],
    available_qualities: [],
    supports_orientation: false,
    photoalbum_covers: [],
    photoalbum_extra_page_price: '',
    photoalbum_default_pages: '',
    has_frame_option: false,
    frame_options: [],
  });

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/admin/products', { headers: getAuthHeaders() });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Kunde inte hämta produkter');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Auto-open the edit modal when navigated here with ?edit=<product_id>
  // (used by the Google Merchant page's "Redigera"-link).
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || products.length === 0) return;
    const target = products.find((p) => p.product_id === editId);
    if (target) {
      openEditModal(target);
      // Strip the query param so closing/reopening modal works as expected
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [products, searchParams, setSearchParams]);

  // Fetch admin-defined categories so the product form's category select picks up new ones
  const [adminCategories, setAdminCategories] = useState([]);
  useEffect(() => {
    api.get('/admin/product-categories', { headers: getAuthHeaders() })
      .then((res) => setAdminCategories(res.data || []))
      .catch(() => {});
  }, [getAuthHeaders]);

  // Built-in categories that map to specific editor model_types — these always exist
  const BUILTIN_CATEGORIES = [
    { id: 'mugg', name: 'Muggar', model_type: 'mug' },
    { id: 'tshirt', name: 'T-shirts', model_type: 'tshirt' },
    { id: 'hoodie', name: 'Hoodies', model_type: 'hoodie' },
    { id: 'poster', name: 'Posters', model_type: 'poster' },
    { id: 'mobilskal', name: 'Mobilskal', model_type: 'phonecase' },
    { id: 'tygkasse', name: 'Tygkassar', model_type: 'totebag' },
    { id: 'kalender', name: 'Kalendrar', model_type: 'calendar' },
    { id: 'namnskylt', name: 'Namnlappar', model_type: 'nametag' },
    { id: 'fotoalbum', name: 'Fotoalbum', model_type: 'photoalbum' },
    { id: 'foretag', name: 'Företag (B2B)', model_type: 'businesscard' },
  ];

  // Merge built-in + admin categories. Built-in keeps model_type mapping; admin custom ones use poster as default.
  const allCategories = (() => {
    const byId = new Map(BUILTIN_CATEGORIES.map((c) => [c.id, c]));
    for (const ac of adminCategories) {
      if (!byId.has(ac.id)) {
        byId.set(ac.id, { id: ac.id, name: ac.name || ac.id, model_type: 'poster' });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Keep legacy sizes in sync with new available_sizes (for cart/editor compatibility)
    const syncedSizes = formData.available_sizes.length > 0 ? formData.available_sizes : formData.sizes;
    
    const usesSizePricing = (formData.available_sizes || []).length > 0 || (formData.size_quality_prices || []).length > 0;
    const productData = {
      ...formData,
      sizes: syncedSizes,
      price: parseFloat(formData.price),
      old_price: usesSizePricing
        ? null
        : (formData.old_price !== '' && formData.old_price != null ? parseFloat(formData.old_price) : null),
      sale_starts_at: localInputToIso(formData.sale_starts_at),
      sale_ends_at: localInputToIso(formData.sale_ends_at),
      images: formData.images.filter(img => img.trim() !== ''),
      photoalbum_extra_page_price: formData.photoalbum_extra_page_price === '' ? null : parseFloat(formData.photoalbum_extra_page_price),
      photoalbum_default_pages: formData.photoalbum_default_pages === '' ? null : parseInt(formData.photoalbum_default_pages, 10),
    };

    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.product_id}`, productData, { headers: getAuthHeaders() });
        toast.success('Produkt uppdaterad');
      } else {
        await api.post('/admin/products', productData, { headers: getAuthHeaders() });
        toast.success('Produkt skapad');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || JSON.stringify(e)).join(' • ')
        : (detail || error?.message || 'Kunde inte spara produkt');
      toast.error(msg);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Är du säker på att du vill ta bort denna produkt?')) return;

    try {
      await api.delete(`/admin/products/${productId}`, { headers: getAuthHeaders() });
      toast.success('Produkt raderad');
      fetchProducts();
    } catch (error) {
      toast.error('Kunde inte radera produkt');
    }
  };

  const handleToggleVisibility = async (productId, isHidden) => {
    try {
      await api.patch(`/admin/products/${productId}/visibility`, {}, { headers: getAuthHeaders() });
      toast.success(isHidden ? 'Produkten är nu synlig för kunder' : 'Produkten är nu gömd för kunder');
      fetchProducts();
    } catch {
      toast.error('Kunde inte ändra synlighet');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    // Auto-migrate legacy sizes to new available_sizes/size_quality_prices on open
    const legacySizes = product.sizes || [];
    const existingNewSizes = product.available_sizes || [];
    const existingSqp = product.size_quality_prices || [];
    let migratedSizes = existingNewSizes;
    let migratedSqp = existingSqp;
    if (legacySizes.length > 0 && existingNewSizes.length === 0 && existingSqp.length === 0) {
      migratedSizes = legacySizes;
      migratedSqp = legacySizes.map(s => ({ size: s, quality: 'Standard', price: product.price || 0 }));
    }
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      long_description: product.long_description || '',
      price: product.price.toString(),
      old_price: product.old_price != null && product.old_price !== '' ? product.old_price.toString() : '',
      sale_starts_at: isoToLocalInput(product.sale_starts_at),
      sale_ends_at: isoToLocalInput(product.sale_ends_at),
      images: product.images.length > 0 ? product.images : [''],
      colors: product.colors || [],
      sizes: product.sizes || [],
      model_type: product.model_type,
      quantity_prices: product.quantity_prices || [],
      color_images: product.color_images || {},
      size_images: product.size_images || {},
      size_quality_prices: migratedSqp,
      available_sizes: migratedSizes,
      available_qualities: product.available_qualities || [],
      supports_orientation: !!product.supports_orientation,
      photoalbum_covers: product.photoalbum_covers || [],
      photoalbum_extra_page_price: product.photoalbum_extra_page_price ?? '',
      photoalbum_default_pages: product.photoalbum_default_pages ?? '',
      has_frame_option: !!product.has_frame_option,
      frame_options: product.frame_options || [],
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      category: '',
      description: '',
      long_description: '',
      price: '',
      old_price: '',
      sale_starts_at: '',
      sale_ends_at: '',
      images: [''],
      colors: [],
      sizes: [],
      model_type: 'mug',
      quantity_prices: [],
      color_images: {},
      size_images: {},
      size_quality_prices: [],
      available_sizes: [],
      available_qualities: [],
      supports_orientation: false,
      photoalbum_covers: [],
      photoalbum_extra_page_price: '',
      photoalbum_default_pages: '',
      has_frame_option: false,
      frame_options: [],
    });
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const updateImage = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, images: newImages.length > 0 ? newImages : [''] }));
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Bara JPG, PNG eller WebP');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max 10MB');
      return;
    }
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await api.post('/upload', formDataUpload, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      updateImage(index, url);
      toast.success('Bild uppladdad!');
    } catch {
      toast.error('Kunde inte ladda upp bilden');
    }
  };

  const handleNewImageUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Bara JPG, PNG eller WebP');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max 10MB');
      return;
    }
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await api.post('/upload', formDataUpload, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      // Replace first empty slot or add new
      const emptyIdx = formData.images.findIndex(img => !img.trim());
      if (emptyIdx >= 0) {
        updateImage(emptyIdx, url);
      } else {
        setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
      }
      toast.success('Bild uppladdad!');
    } catch {
      toast.error('Kunde inte ladda upp bilden');
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' ||
      (categoryFilter === 'b2b' && product.category === 'foretag') ||
      (categoryFilter === 'shop' && product.category !== 'foretag');
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{view === 'products' ? 'Produkter' : 'Kategorier'}</h1>
          <p className="text-slate-500">{view === 'products' ? 'Hantera dina produkter och lager' : 'Hantera produktkategorier (slug, namn, bild)'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setView('products')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'products' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
              data-testid="view-products-btn"
            >
              <Package className="w-3.5 h-3.5 inline mr-1.5" /> Produkter
            </button>
            <button
              onClick={() => setView('categories')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'categories' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
              data-testid="view-categories-btn"
            >
              <FolderTree className="w-3.5 h-3.5 inline mr-1.5" /> Kategorier
            </button>
          </div>
          {view === 'products' && (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowBulkSaleModal(true)}
                variant="outline"
                className="border-[#e76f51] text-[#e76f51] hover:bg-[#e76f51] hover:text-white"
                data-testid="open-bulk-sale-btn"
              >
                <Tag className="w-4 h-4 mr-2" /> Bulk-rea
              </Button>
              <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Lägg till produkt
              </Button>
            </div>
          )}
        </div>
      </div>

      {view === 'categories' ? (
        <AdminCategories onChange={() => {
          // Refresh the category dropdown after admin adds/removes categories
          api.get('/admin/product-categories', { headers: getAuthHeaders() })
            .then((res) => setAdminCategories(res.data || []))
            .catch(() => {});
        }} />
      ) : (
        <>
      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Sök produkter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[
            { value: 'all', label: 'Alla' },
            { value: 'shop', label: 'Webbshop' },
            { value: 'b2b', label: 'Företag' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setCategoryFilter(f.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${categoryFilter === f.value ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`filter-${f.value}`}
            >
              {f.value === 'b2b' && <Briefcase className="w-3.5 h-3.5 inline mr-1" />}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <div 
            key={product.product_id}
            className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden group relative ${product.hidden ? 'opacity-60' : ''}`}
          >
            {product.hidden && (
              <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-700 text-white shadow" data-testid={`hidden-badge-${product.product_id}`}>
                <EyeOff className="w-3 h-3" /> GÖMD
              </span>
            )}
            <div className="aspect-square bg-slate-100 relative">
              {product.images?.[0] ? (
                <img 
                  src={resolveImageUrl(product.images[0])} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                />
              ) : null}
              {!product.images?.[0] && (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-12 h-12 text-slate-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => openEditModal(product)}
                  data-testid={`edit-product-${product.product_id}`}
                  title="Redigera"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button 
                  variant="secondary"
                  size="sm"
                  onClick={() => handleToggleVisibility(product.product_id, product.hidden)}
                  data-testid={`toggle-visibility-${product.product_id}`}
                  title={product.hidden ? 'Visa för kunder' : 'Göm för kunder'}
                >
                  {product.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleDelete(product.product_id)}
                  data-testid={`delete-product-${product.product_id}`}
                  title="Radera"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-slate-900 truncate flex-1">{product.name}</h3>
                {product.category === 'foretag' && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-100 text-indigo-700 shrink-0">
                    <Briefcase className="w-2.5 h-2.5" />B2B
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">{product.category}</p>
              {product.colors?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {product.colors.map((c, i) => (
                    <span key={`color-${c}-${i}`} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>
                  ))}
                </div>
              )}
              {product.sizes?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {product.sizes.map((s, i) => (
                    <span key={`size-${s}-${i}`} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{s}</span>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <p className="text-lg font-bold text-primary">{product.price} kr</p>
                {product.old_price && Number(product.old_price) > Number(product.price) && (
                  <span className="text-sm text-slate-400 line-through">{Number(product.old_price).toLocaleString('sv-SE')} kr</span>
                )}
              </div>
              {(() => {
                if (!product.old_price || Number(product.old_price) <= Number(product.price)) return null;
                const now = Date.now();
                const startsMs = product.sale_starts_at ? Date.parse(product.sale_starts_at) : null;
                const endsMs = product.sale_ends_at ? Date.parse(product.sale_ends_at) : null;
                const active = (!startsMs || now >= startsMs) && (!endsMs || now <= endsMs);
                const pct = Math.round(((Number(product.old_price) - Number(product.price)) / Number(product.old_price)) * 100);
                let label;
                let color;
                if (startsMs && now < startsMs) {
                  label = `Rea planerad från ${new Date(startsMs).toLocaleDateString('sv-SE')}`;
                  color = 'bg-slate-100 text-slate-600';
                } else if (endsMs && now > endsMs) {
                  label = `Rea avslutad ${new Date(endsMs).toLocaleDateString('sv-SE')}`;
                  color = 'bg-slate-100 text-slate-400';
                } else if (active) {
                  const endsStr = endsMs ? ` (t.o.m. ${new Date(endsMs).toLocaleDateString('sv-SE')})` : '';
                  label = `-${pct}% aktiv${endsStr}`;
                  color = 'bg-[#e76f51]/10 text-[#e76f51] border border-[#e76f51]/20';
                } else {
                  label = `-${pct}% (utan datum)`;
                  color = 'bg-[#e76f51]/10 text-[#e76f51] border border-[#e76f51]/20';
                }
                return (
                  <span className={`inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded ${color}`} data-testid={`sale-status-${product.product_id}`}>
                    🏷️ {label}
                  </span>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p>Inga produkter hittades</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingProduct ? 'Redigera produkt' : 'Lägg till produkt'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Produktnamn</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => {
                      const cat = allCategories.find((c) => c.id === value);
                      setFormData(prev => ({
                        ...prev,
                        category: value,
                        model_type: cat?.model_type || prev.model_type,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Välj kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Kort beskrivning <span className="text-slate-400 text-xs font-normal">(visas under produktrubriken)</span></label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                  placeholder="Kort beskrivning som visas direkt under produktrubriken..."
                  minHeight={120}
                  testId="product-short-description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Detaljerad beskrivning <span className="text-slate-400 text-xs font-normal">(expanderbar sektion på produktsidan — valfritt)</span></label>
                <RichTextEditor
                  value={formData.long_description}
                  onChange={(html) => setFormData(prev => ({ ...prev, long_description: html }))}
                  placeholder="Material, storlek, skötselråd, leverans, garantier ..."
                  minHeight={180}
                  testId="product-long-description"
                />
              </div>

              {(() => {
                const usesSizePricing = (formData.available_sizes || []).length > 0 || (formData.size_quality_prices || []).length > 0;
                if (usesSizePricing) {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Pris (kr)</label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        min="0"
                        step="0.01"
                        required
                        data-testid="product-price-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">Ordinarie pris sätts per storlek nedan i "Storlekar &amp; Priser".</p>
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Pris (kr)</label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        min="0"
                        step="0.01"
                        required
                        data-testid="product-price-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Ordinarie pris (kr) <span className="text-slate-400 text-xs font-normal">— visas överstruket, valfritt</span>
                      </label>
                      <Input
                        type="number"
                        value={formData.old_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, old_price: e.target.value }))}
                        min="0"
                        step="0.01"
                        placeholder="t.ex. 299"
                        data-testid="product-old-price-input"
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Datum-styrd rea (valfritt) — priset visas som rea först när nuvarande tid ligger inom fönstret */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-orange-50/50 border border-orange-100 rounded-lg">
                <div className="md:col-span-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <span>🏷️ Datum-styrd rea</span>
                  <span className="text-xs font-normal text-slate-400">— lämna tomt för att alltid visa rean när ordinarie pris är satt</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Från</label>
                  <Input
                    type="datetime-local"
                    value={formData.sale_starts_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, sale_starts_at: e.target.value }))}
                    data-testid="product-sale-starts-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Till</label>
                  <Input
                    type="datetime-local"
                    value={formData.sale_ends_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, sale_ends_at: e.target.value }))}
                    data-testid="product-sale-ends-input"
                  />
                </div>
              </div>

              {/* Orientation support (fotoutskrift / poster) */}
              {['poster', 'canvas'].includes(formData.category) && (
                <label className="flex items-center gap-2 text-sm p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.supports_orientation}
                    onChange={(e) => setFormData(prev => ({ ...prev, supports_orientation: e.target.checked }))}
                    className="w-4 h-4 accent-[#2a9d8f]"
                    data-testid="supports-orientation-toggle"
                  />
                  <span className="font-medium text-slate-700">Låt kunden välja liggande eller stående i designern</span>
                </label>
              )}

              {/* Quantity-based pricing for business cards */}
              {formData.model_type === 'businesscard' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <span className="flex items-center gap-1.5">Stafflade priser (antal → pris)</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-2">Priset baseras på antal beställda visitkort</p>
                  {formData.quantity_prices.map((qp, idx) => (
                    <div key={`qp-${idx}`} className="flex gap-2 mb-2 items-center">
                      <Input
                        type="number"
                        value={qp.quantity || ''}
                        onChange={(e) => {
                          const next = [...formData.quantity_prices];
                          next[idx] = { ...next[idx], quantity: parseInt(e.target.value) || 0 };
                          setFormData(prev => ({ ...prev, quantity_prices: next }));
                        }}
                        placeholder="Antal"
                        className="w-24"
                        min="1"
                        data-testid={`qp-quantity-${idx}`}
                      />
                      <span className="text-sm text-slate-400">st →</span>
                      <Input
                        type="number"
                        value={qp.price || ''}
                        onChange={(e) => {
                          const next = [...formData.quantity_prices];
                          next[idx] = { ...next[idx], price: parseFloat(e.target.value) || 0 };
                          setFormData(prev => ({ ...prev, quantity_prices: next }));
                        }}
                        placeholder="Pris (kr)"
                        className="w-28"
                        min="0"
                        step="0.01"
                        data-testid={`qp-price-${idx}`}
                      />
                      <span className="text-sm text-slate-400">kr</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const next = formData.quantity_prices.filter((_, i) => i !== idx);
                          setFormData(prev => ({ ...prev, quantity_prices: next }));
                        }}
                        data-testid={`qp-remove-${idx}`}
                      >
                        <X className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      quantity_prices: [...prev.quantity_prices, { quantity: 0, price: 0 }]
                    }))}
                    data-testid="add-quantity-price"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Lägg till prissteg
                  </Button>
                </div>
              )}

              {/* Quality (paper/material) — adds a price column per size */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> Kvaliteter (papper/material)</span>
                </label>
                <p className="text-xs text-slate-400 mb-2">Valfritt — t.ex. Standard, Premium, Canvas. Varje storlek får ett pris per kvalitet.</p>
                <TagInput
                  tags={formData.available_qualities}
                  onChange={(newQualities) => {
                    setFormData(prev => {
                      const oldQualities = prev.available_qualities;
                      const sizes = prev.available_sizes;
                      // Rebuild size_quality_prices to match the new qualities × sizes grid
                      let nextPrices = [...prev.size_quality_prices];
                      const effectiveOld = oldQualities.length > 0 ? oldQualities : ['Standard'];
                      const effectiveNew = newQualities.length > 0 ? newQualities : ['Standard'];
                      // Remove entries with qualities no longer present
                      nextPrices = nextPrices.filter(p => effectiveNew.includes(p.quality));
                      // Add missing entries for each size × new quality
                      for (const s of sizes) {
                        for (const q of effectiveNew) {
                          if (!nextPrices.some(p => p.size === s && p.quality === q)) {
                            // Default price = inherit from old quality price (if any) or product price
                            const fallback = (nextPrices.find(p => p.size === s)?.price)
                              ?? (prev.size_quality_prices.find(p => p.size === s && effectiveOld.includes(p.quality))?.price)
                              ?? (parseFloat(prev.price) || 0);
                            nextPrices.push({ size: s, quality: q, price: fallback });
                          }
                        }
                      }
                      return { ...prev, available_qualities: newQualities, size_quality_prices: nextPrices };
                    });
                  }}
                  placeholder="T.ex. Standard, Premium, Canvas + Enter"
                  testId="quality"
                />
              </div>

              {/* Size/Price per product — grouped by size, with one row per quality */}
              <div data-testid="size-quality-section">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" /> Storlekar & Priser</span>
                </label>
                <p className="text-xs text-slate-400 mb-3">Lägg till storlekar — varje rad är ett pris för en specifik storlek (och kvalitet om sådana finns).</p>
                <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  💡 <strong>Så visas rea:</strong> Sätt <strong>Ord. pris högre</strong> än priset. Ex: Pris=<code className="bg-white px-1 rounded">229</code>, Ord. pris=<code className="bg-white px-1 rounded">299</code> → kund ser <span className="font-semibold text-[#e76f51]">229 kr <span className="line-through text-slate-400">299 kr</span> -23%</span>.
                </div>
                <div className="hidden md:flex items-center gap-2 mb-2 px-3 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {formData.available_qualities.length > 0 && <span className="w-24 shrink-0"></span>}
                  <span className="flex-1">Aktuellt pris (kr)</span>
                  <span className="flex-1">Ord. pris (kr) <span className="normal-case text-slate-300">— högre för rea, valfritt</span></span>
                </div>
                <div className="space-y-3">
                  {formData.available_sizes.map((sizeName) => {
                    const qualities = formData.available_qualities.length > 0 ? formData.available_qualities : ['Standard'];
                    const showQualityLabel = formData.available_qualities.length > 0;
                    return (
                      <div key={sizeName} className="bg-slate-50 rounded-lg p-3" data-testid={`size-block-${sizeName}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-700">{sizeName}</span>
                          <button type="button" onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              available_sizes: prev.available_sizes.filter(s => s !== sizeName),
                              size_quality_prices: prev.size_quality_prices.filter(p => p.size !== sizeName)
                            }));
                          }} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 shrink-0" data-testid={`remove-size-${sizeName}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-1.5">
                          {qualities.map(q => {
                            const entry = formData.size_quality_prices.find(p => p.size === sizeName && p.quality === q);
                            const priceNum = Number(entry?.price);
                            const oldNum = entry?.old_price != null && entry.old_price !== '' ? Number(entry.old_price) : null;
                            const invertedWarning = oldNum != null && !Number.isNaN(oldNum) && oldNum > 0 && !Number.isNaN(priceNum) && priceNum > 0 && oldNum <= priceNum;
                            const validDiscountPct = oldNum != null && !Number.isNaN(oldNum) && oldNum > priceNum && priceNum > 0
                              ? Math.round(((oldNum - priceNum) / oldNum) * 100)
                              : null;
                            return (
                              <div key={`${sizeName}-${q}`} className="space-y-1">
                                <div className="flex items-center gap-2">
                                  {showQualityLabel && <span className="text-xs text-slate-500 w-24 shrink-0">{q}</span>}
                                  <div className="flex items-center gap-1 flex-1">
                                    <Input
                                      type="number" min="0" step="0.01"
                                      value={entry?.price ?? ''}
                                      onChange={e => {
                                        const newPrice = parseFloat(e.target.value) || 0;
                                        setFormData(prev => {
                                          const next = [...prev.size_quality_prices];
                                          const existingIdx = next.findIndex(p => p.size === sizeName && p.quality === q);
                                          if (existingIdx >= 0) {
                                            next[existingIdx] = { ...next[existingIdx], price: newPrice };
                                          } else {
                                            next.push({ size: sizeName, quality: q, price: newPrice });
                                          }
                                          return { ...prev, size_quality_prices: next };
                                        });
                                      }}
                                      placeholder="Pris"
                                      className="h-9 text-sm"
                                      data-testid={`price-${sizeName}-${q}`}
                                    />
                                    <span className="text-sm text-slate-500 shrink-0">kr</span>
                                  </div>
                                  <div className="flex items-center gap-1 flex-1">
                                    <Input
                                      type="number" min="0" step="0.01"
                                      value={entry?.old_price ?? ''}
                                      onChange={e => {
                                        const val = e.target.value;
                                        setFormData(prev => {
                                          const next = [...prev.size_quality_prices];
                                          const existingIdx = next.findIndex(p => p.size === sizeName && p.quality === q);
                                          const parsed = val === '' ? null : parseFloat(val);
                                          if (existingIdx >= 0) {
                                            next[existingIdx] = { ...next[existingIdx], old_price: parsed };
                                          } else {
                                            next.push({ size: sizeName, quality: q, price: 0, old_price: parsed });
                                          }
                                          return { ...prev, size_quality_prices: next };
                                        });
                                      }}
                                      placeholder="Ord. pris (högre)"
                                      className={`h-9 text-sm bg-white/60 ${invertedWarning ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                                      data-testid={`old-price-${sizeName}-${q}`}
                                    />
                                    <span className="text-xs text-slate-400 shrink-0 line-through">kr</span>
                                  </div>
                                </div>
                                {invertedWarning && (
                                  <p className="text-xs text-red-600 pl-1 flex items-center gap-1" data-testid={`old-price-warning-${sizeName}-${q}`}>
                                    ⚠️ Ord. pris ({oldNum} kr) måste vara <strong>högre</strong> än priset ({priceNum} kr) för att visa som rea.
                                  </p>
                                )}
                                {validDiscountPct != null && validDiscountPct > 0 && (
                                  <p className="text-xs text-[#e76f51] pl-1" data-testid={`discount-preview-${sizeName}-${q}`}>
                                    ✓ Visas som <strong>−{validDiscountPct}% rea</strong> för kunden
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <SizeAddInput
                  existingSizes={formData.available_sizes}
                  onAdd={(v) => setFormData(prev => {
                    const qualities = prev.available_qualities.length > 0 ? prev.available_qualities : ['Standard'];
                    const newEntries = qualities.map(q => ({ size: v, quality: q, price: parseFloat(prev.price) || 0 }));
                    return {
                      ...prev,
                      available_sizes: [...prev.available_sizes, v],
                      size_quality_prices: [...prev.size_quality_prices, ...newEntries]
                    };
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <span className="flex items-center gap-1.5"><Palette className="w-4 h-4" /> Färger</span>
                </label>
                <TagInput
                  tags={formData.colors}
                  onChange={(colors) => setFormData(prev => ({ ...prev, colors }))}
                  placeholder="Skriv färg + Enter"
                  testId="color"
                />
              </div>

              {/* Photo album: covers + page pricing — shown when model_type or category indicates a photoalbum */}
              {(formData.model_type === 'photoalbum' || formData.category?.toLowerCase().includes('fotoalbum')) && (
                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4" /> Fotoalbum-inställningar
                  </h3>

                  {/* Pages config */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Inkluderade sidor (standard)</label>
                      <Input
                        type="number" min="1"
                        value={formData.photoalbum_default_pages}
                        onChange={(e) => setFormData(prev => ({ ...prev, photoalbum_default_pages: e.target.value }))}
                        placeholder="t.ex. 20"
                        data-testid="album-default-pages-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Pris per extra sida (kr)</label>
                      <Input
                        type="number" min="0" step="0.5"
                        value={formData.photoalbum_extra_page_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, photoalbum_extra_page_price: e.target.value }))}
                        placeholder="t.ex. 5"
                        data-testid="album-extra-page-price-input"
                      />
                    </div>
                  </div>

                  {/* Cover materials list */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-slate-600">Fodral / Omslag</label>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          photoalbum_covers: [...(prev.photoalbum_covers || []), { id: '', label: '', desc: '', price: 0 }],
                        }))}
                        className="text-xs text-[#2a9d8f] hover:underline flex items-center gap-1"
                        data-testid="album-add-cover-btn"
                      >
                        <Plus className="w-3.5 h-3.5" /> Lägg till fodral
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(formData.photoalbum_covers || []).map((cover, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200 space-y-2" data-testid={`album-cover-row-${idx}`}>
                          <div className="flex items-start gap-3">
                            {/* Cover image upload */}
                            <label className="block w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#2a9d8f] cursor-pointer overflow-hidden bg-slate-50 relative group flex-shrink-0">
                              {cover.image ? (
                                <>
                                  <img src={resolveImageUrl(cover.image)} alt="" className="w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-white" />
                                  </div>
                                </>
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                                  <Upload className="w-4 h-4" />
                                  <span className="text-[9px] mt-1">Bild</span>
                                </div>
                              )}
                              <input
                                type="file" accept="image/*" className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  try {
                                    const fd = new FormData();
                                    fd.append('file', file);
                                    const res = await api.post('/upload', fd, {
                                      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
                                    });
                                    setFormData(prev => {
                                      const arr = [...prev.photoalbum_covers];
                                      arr[idx] = { ...arr[idx], image: res.data.url };
                                      return { ...prev, photoalbum_covers: arr };
                                    });
                                    toast.success('Fodralbild uppladdad');
                                  } catch {
                                    toast.error('Kunde inte ladda upp');
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>

                            {/* Name/desc/price */}
                            <div className="flex-1 space-y-2 min-w-0">
                              <Input
                                value={cover.label || ''}
                                onChange={(e) => setFormData(prev => {
                                  const arr = [...prev.photoalbum_covers];
                                  arr[idx] = { ...arr[idx], label: e.target.value, id: arr[idx].id || e.target.value.toLowerCase().replace(/\s+/g, '_') };
                                  return { ...prev, photoalbum_covers: arr };
                                })}
                                placeholder="Namn (t.ex. Tygbeklätt)"
                                className="text-sm"
                              />
                              <div className="grid grid-cols-[1fr_100px] gap-2">
                                <Input
                                  value={cover.desc || ''}
                                  onChange={(e) => setFormData(prev => {
                                    const arr = [...prev.photoalbum_covers];
                                    arr[idx] = { ...arr[idx], desc: e.target.value };
                                    return { ...prev, photoalbum_covers: arr };
                                  })}
                                  placeholder="Beskrivning"
                                  className="text-sm"
                                />
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={cover.price ?? 0}
                                  onChange={(e) => setFormData(prev => {
                                    const arr = [...prev.photoalbum_covers];
                                    arr[idx] = { ...arr[idx], price: parseFloat(e.target.value) || 0 };
                                    return { ...prev, photoalbum_covers: arr };
                                  })}
                                  placeholder="Pris"
                                  className="text-sm"
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                photoalbum_covers: prev.photoalbum_covers.filter((_, i) => i !== idx),
                              }))}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 flex-shrink-0"
                              data-testid={`album-cover-remove-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Color options for this cover */}
                          <div className="border-t border-slate-100 pt-2">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[11px] font-medium text-slate-600">Färgalternativ</span>
                              <span className="text-[10px] text-slate-400">{(cover.colors || []).length} färger</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {(cover.colors || []).map((color, colorIdx) => (
                                <div key={colorIdx} className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full pl-1 pr-2 py-0.5">
                                  <input
                                    type="color"
                                    value={color.hex || '#000000'}
                                    onChange={(e) => setFormData(prev => {
                                      const arr = [...prev.photoalbum_covers];
                                      const colors = [...(arr[idx].colors || [])];
                                      colors[colorIdx] = { ...colors[colorIdx], hex: e.target.value };
                                      arr[idx] = { ...arr[idx], colors };
                                      return { ...prev, photoalbum_covers: arr };
                                    })}
                                    className="w-5 h-5 rounded-full border-0 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={color.name || ''}
                                    onChange={(e) => setFormData(prev => {
                                      const arr = [...prev.photoalbum_covers];
                                      const colors = [...(arr[idx].colors || [])];
                                      colors[colorIdx] = { ...colors[colorIdx], name: e.target.value };
                                      arr[idx] = { ...arr[idx], colors };
                                      return { ...prev, photoalbum_covers: arr };
                                    })}
                                    placeholder="Svart"
                                    className="text-[11px] bg-transparent w-16 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => {
                                      const arr = [...prev.photoalbum_covers];
                                      arr[idx] = { ...arr[idx], colors: (arr[idx].colors || []).filter((_, i) => i !== colorIdx) };
                                      return { ...prev, photoalbum_covers: arr };
                                    })}
                                    className="text-slate-400 hover:text-red-500"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => setFormData(prev => {
                                  const arr = [...prev.photoalbum_covers];
                                  const colors = [...(arr[idx].colors || []), { name: 'Ny färg', hex: '#000000' }];
                                  arr[idx] = { ...arr[idx], colors };
                                  return { ...prev, photoalbum_covers: arr };
                                })}
                                className="text-[11px] text-[#2a9d8f] hover:underline flex items-center gap-0.5"
                                data-testid={`album-cover-add-color-${idx}`}
                              >
                                <Plus className="w-3 h-3" /> Lägg färg
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(formData.photoalbum_covers || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic">Inga fodral tillagda — kunden får ett standardomslag.</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Det första fodralet i listan blir standardvalet. Pris adderas på totalpriset.</p>
                  </div>
                </div>
              )}

              {/* Frame options (for posters / canvas / wall art) */}
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Image className="w-4 h-4" /> Ramalternativ (tillval)
                  </h3>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={formData.has_frame_option}
                      onChange={(e) => setFormData(prev => ({ ...prev, has_frame_option: e.target.checked }))}
                      data-testid="frame-option-toggle"
                    />
                    Aktivera ram-tillval
                  </label>
                </div>

                {formData.has_frame_option && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-slate-600">Ramar (storlek + pris)</label>
                      <div className="flex items-center gap-2">
                        <select
                          onChange={async (e) => {
                            const srcId = e.target.value;
                            if (!srcId) return;
                            try {
                              const res = await api.get(`/products/${srcId}`);
                              const srcFrames = res.data?.frame_options || [];
                              if (srcFrames.length === 0) {
                                toast.error('Den valda produkten har inga ramar att kopiera');
                                return;
                              }
                              setFormData((prev) => ({ ...prev, frame_options: [...(prev.frame_options || []), ...srcFrames.map((f) => ({ ...f, id: `${f.id || 'frame'}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }))] }));
                              toast.success(`Kopierade ${srcFrames.length} ramar`);
                            } catch {
                              toast.error('Kunde inte hämta ramar');
                            }
                            e.target.value = '';
                          }}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                          data-testid="frame-copy-from-select"
                        >
                          <option value="">📋 Kopiera ramar från...</option>
                          {products.filter((p) => p.has_frame_option && p.product_id !== formData.product_id).map((p) => (
                            <option key={p.product_id} value={p.product_id}>
                              {p.name} ({(p.frame_options || []).length} ramar)
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            frame_options: [...(prev.frame_options || []), { id: `frame-${Date.now()}`, label: '', price: 0, image: '' }],
                          }))}
                          className="text-xs text-[#2a9d8f] hover:underline flex items-center gap-1"
                          data-testid="frame-option-add-btn"
                        >
                          <Plus className="w-3 h-3" /> Lägg till ram
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {(formData.frame_options || []).map((frame, idx) => (
                        <div key={frame.id || idx} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`frame-option-row-${idx}`}>
                          <div className="flex items-start gap-3">
                            {/* Image preview / upload */}
                            <div className="flex-shrink-0">
                              <label className="block w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#2a9d8f] cursor-pointer overflow-hidden bg-slate-50 relative group" data-testid={`frame-option-upload-${idx}`}>
                                {frame.image ? (
                                  <>
                                    <img src={resolveImageUrl(frame.image)} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Upload className="w-4 h-4 text-white" />
                                    </div>
                                  </>
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                                    <Upload className="w-4 h-4" />
                                    <span className="text-[10px] mt-1">Ladda upp</span>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    try {
                                      const fd = new FormData();
                                      fd.append('file', file);
                                      const res = await api.post('/upload', fd, {
                                        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
                                      });
                                      setFormData(prev => {
                                        const next = [...prev.frame_options];
                                        next[idx] = { ...next[idx], image: res.data.url };
                                        return { ...prev, frame_options: next };
                                      });
                                      toast.success('Ram-bild uppladdad');
                                    } catch {
                                      toast.error('Kunde inte ladda upp bilden');
                                    }
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            </div>

                            {/* Name + price */}
                            <div className="flex-1 space-y-2 min-w-0">
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Namn (storlek)</label>
                                <Input
                                  value={frame.label || ''}
                                  onChange={(e) => setFormData(prev => {
                                    const next = [...prev.frame_options];
                                    next[idx] = { ...next[idx], label: e.target.value };
                                    return { ...prev, frame_options: next };
                                  })}
                                  placeholder="t.ex. Ek 30 × 40 cm"
                                  className="text-sm"
                                  data-testid={`frame-option-label-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Pris (kr)</label>
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={frame.price ?? 0}
                                  onChange={(e) => setFormData(prev => {
                                    const next = [...prev.frame_options];
                                    next[idx] = { ...next[idx], price: parseFloat(e.target.value) || 0 };
                                    return { ...prev, frame_options: next };
                                  })}
                                  placeholder="299"
                                  className="text-sm w-32"
                                  data-testid={`frame-option-price-${idx}`}
                                />
                              </div>
                            </div>

                            {/* Remove */}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                frame_options: prev.frame_options.filter((_, i) => i !== idx),
                              }))}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 flex-shrink-0"
                              data-testid={`frame-option-remove-${idx}`}
                              title="Ta bort"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {(formData.frame_options || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic">Inga ramar tillagda — lägg till minst en för att kunden ska kunna välja.</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Kunden ser en &quot;Lägg till ram&quot;-väljare i editorn. Priset adderas på totalpriset i kassan.</p>
                  </>
                )}
              </div>

              {/* Color-specific images */}
              {formData.colors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <span className="flex items-center gap-1.5"><Image className="w-4 h-4" /> Bild per färg</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-3">Ladda upp en bild för varje färg — visas när kunden väljer färgen</p>
                  <div className="space-y-2">
                    {formData.colors.map(color => (
                      <div key={color} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2" data-testid={`color-image-${color}`}>
                        <span className="text-sm font-medium text-slate-700 w-24 shrink-0">{color}</span>
                        {formData.color_images[color] ? (
                          <div className="flex items-center gap-2 flex-1">
                            <img src={resolveImageUrl(formData.color_images[color])} alt={color} className="w-10 h-10 rounded object-cover border" />
                            <span className="text-xs text-slate-400 truncate flex-1">{formData.color_images[color].split('/').pop()}</span>
                            <button type="button" onClick={() => setFormData(prev => {
                              const ci = { ...prev.color_images };
                              delete ci[color];
                              return { ...prev, color_images: ci };
                            })} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex-1 flex items-center justify-center gap-1.5 border border-dashed border-slate-300 hover:border-[#2a9d8f] rounded-lg py-2 cursor-pointer text-xs text-slate-400 hover:text-[#2a9d8f]">
                            <Upload className="w-3.5 h-3.5" /> Ladda upp
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const res = await api.post('/upload', fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                                const url = res.data.url;
                                setFormData(prev => ({ ...prev, color_images: { ...prev.color_images, [color]: url } }));
                              } catch { toast.error('Uppladdning misslyckades'); }
                            }} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Size-specific images — works for ALL products with sizes, existing or new */}
              {(formData.available_sizes.length > 0 || formData.sizes.length > 0) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <span className="flex items-center gap-1.5"><Image className="w-4 h-4" /> Bild per storlek</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-3">Ladda upp en bild per storlek — visas när kunden väljer storleken i produktsidan/designern</p>
                  <div className="space-y-2">
                    {(formData.available_sizes.length > 0 ? formData.available_sizes : formData.sizes).map(sz => (
                      <div key={sz} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2" data-testid={`size-image-${sz}`}>
                        <span className="text-sm font-medium text-slate-700 w-24 shrink-0">{sz}</span>
                        {formData.size_images?.[sz] ? (
                          <div className="flex items-center gap-2 flex-1">
                            <img src={resolveImageUrl(formData.size_images[sz])} alt={sz} className="w-10 h-10 rounded object-cover border" />
                            <span className="text-xs text-slate-400 truncate flex-1">{formData.size_images[sz].split('/').pop()}</span>
                            <button type="button" onClick={() => setFormData(prev => {
                              const si = { ...(prev.size_images || {}) };
                              delete si[sz];
                              return { ...prev, size_images: si };
                            })} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`remove-size-image-${sz}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex-1 flex items-center justify-center gap-1.5 border border-dashed border-slate-300 hover:border-[#2a9d8f] rounded-lg py-2 cursor-pointer text-xs text-slate-400 hover:text-[#2a9d8f]" data-testid={`upload-size-image-${sz}`}>
                            <Upload className="w-3.5 h-3.5" /> Ladda upp
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const res = await api.post('/upload', fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                                const url = res.data.url;
                                setFormData(prev => ({ ...prev, size_images: { ...(prev.size_images || {}), [sz]: url } }));
                                toast.success('Bild uppladdad');
                              } catch { toast.error('Uppladdning misslyckades'); }
                            }} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Produktbilder</label>

                {/* Uploaded images grid */}
                {formData.images.some(img => img.trim()) && (
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {formData.images.map((img, index) => (
                      img.trim() ? (
                        <div key={`img-${index}`} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                          <img
                            src={resolveImageUrl(img)}
                            alt={`Bild ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = ''; e.target.alt = 'Kunde inte ladda'; }}
                            data-testid={`image-preview-${index}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`image-remove-${index}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                          {index === 0 && (
                            <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Huvudbild</span>
                          )}
                        </div>
                      ) : null
                    ))}
                  </div>
                )}

                {/* Upload button */}
                <label
                  className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-300 hover:border-[#2a9d8f] rounded-xl p-5 cursor-pointer transition-colors bg-white group"
                  data-testid="image-upload-area"
                >
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-lg bg-[#2a9d8f]/10 group-hover:bg-[#2a9d8f]/20 flex items-center justify-center transition-colors">
                      <Upload className="w-5 h-5 text-[#2a9d8f]" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">Klicka för att ladda upp bilder</span>
                    <span className="text-xs text-slate-400">JPG, PNG eller WebP — max 10MB</span>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(f => handleNewImageUpload(f));
                      e.target.value = '';
                    }}
                    data-testid="image-file-input"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Avbryt
                </Button>
                <Button type="submit">
                  {editingProduct ? 'Spara ändringar' : 'Skapa produkt'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showBulkSaleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-sale-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#e76f51]" /> Bulk-rea
              </h3>
              <button onClick={() => setShowBulkSaleModal(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-bulk-sale-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-500">
                Sätter <strong>ordinarie pris</strong> automatiskt på alla produkter så exakt rabatten nedan visas på varje produkt.
              </p>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                <select
                  value={bulkSaleData.category}
                  onChange={(e) => setBulkSaleData(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="bulk-sale-category"
                >
                  <option value="all">Alla kategorier</option>
                  {adminCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rabatt (%)</label>
                <Input
                  type="number" min="1" max="99" step="1"
                  value={bulkSaleData.discount_percent}
                  onChange={(e) => setBulkSaleData(p => ({ ...p, discount_percent: e.target.value }))}
                  placeholder="20"
                  data-testid="bulk-sale-percent"
                />
                <p className="text-xs text-slate-400 mt-1">Ex: 20 → produkt 149 kr får ordinarie 186 kr och visas som −20%</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Från (valfritt)</label>
                  <Input
                    type="datetime-local"
                    value={bulkSaleData.sale_starts_at}
                    onChange={(e) => setBulkSaleData(p => ({ ...p, sale_starts_at: e.target.value }))}
                    data-testid="bulk-sale-starts"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Till (valfritt)</label>
                  <Input
                    type="datetime-local"
                    value={bulkSaleData.sale_ends_at}
                    onChange={(e) => setBulkSaleData(p => ({ ...p, sale_ends_at: e.target.value }))}
                    data-testid="bulk-sale-ends"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 p-5 border-t bg-slate-50">
              <Button
                type="button"
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-white"
                disabled={bulkSaleBusy}
                onClick={async () => {
                  if (!window.confirm(`Ta bort rea från ${bulkSaleData.category === 'all' ? 'ALLA produkter' : 'kategori "' + bulkSaleData.category + '"'}?`)) return;
                  setBulkSaleBusy(true);
                  try {
                    const res = await api.post('/admin/products/bulk-sale/clear',
                      { category: bulkSaleData.category },
                      { headers: getAuthHeaders() });
                    toast.success(`Rea rensad för ${res.data.affected} produkter`);
                    fetchProducts();
                    setShowBulkSaleModal(false);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Kunde inte rensa rea');
                  } finally {
                    setBulkSaleBusy(false);
                  }
                }}
                data-testid="bulk-sale-clear-btn"
              >
                Rensa rea
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button" variant="outline"
                  onClick={() => setShowBulkSaleModal(false)}
                  disabled={bulkSaleBusy}
                >
                  Avbryt
                </Button>
                <Button
                  type="button"
                  disabled={bulkSaleBusy}
                  onClick={async () => {
                    const pct = parseFloat(bulkSaleData.discount_percent);
                    if (!pct || pct <= 0 || pct >= 100) {
                      toast.error('Ange en giltig rabatt mellan 1 och 99%');
                      return;
                    }
                    setBulkSaleBusy(true);
                    try {
                      const payload = {
                        category: bulkSaleData.category,
                        discount_percent: pct,
                        sale_starts_at: bulkSaleData.sale_starts_at ? new Date(bulkSaleData.sale_starts_at).toISOString() : null,
                        sale_ends_at: bulkSaleData.sale_ends_at ? new Date(bulkSaleData.sale_ends_at).toISOString() : null,
                      };
                      const res = await api.post('/admin/products/bulk-sale', payload, { headers: getAuthHeaders() });
                      toast.success(`${res.data.affected} produkter fick −${pct}% rabatt`);
                      fetchProducts();
                      setShowBulkSaleModal(false);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Kunde inte tillämpa rea');
                    } finally {
                      setBulkSaleBusy(false);
                    }
                  }}
                  data-testid="bulk-sale-apply-btn"
                >
                  {bulkSaleBusy ? 'Arbetar…' : `Tillämpa −${bulkSaleData.discount_percent || 0}%`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminProducts;
